/*
 * Intelligent Fabric — Admin Console: app wiring.
 *
 * Based on the Backstage 1.32.x `create-app` template, with the IF
 * customizations flagged inline ([IF]). After running `create-app`
 * (MIGRATION.md §1), reconcile this against the generated App.tsx — keep the
 * generated import paths/versions, layer the [IF] blocks on top.
 *
 * [IF] additions vs. the stock template:
 *   - intelligentFabricTheme registered as the sole (dark) theme
 *   - HomePage is the index route ("/" → "/home")
 *   - 5 placeholder routes for the operator surfaces (Phase 30/31 plugins)
 *   - OIDC SignInPage (Keycloak) — guest provider intentionally absent
 *   - ArgoCD + Grafana plugin routes (retired from the Dockerfile sed hack)
 */
import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis, oidcAuthApiRef } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';

// [IF] theme + branded surfaces
import { UnifiedThemeProvider } from '@backstage/theme';
import { intelligentFabricTheme } from './theme/intelligentFabricTheme';
import { HomePage } from './components/home/HomePage';
import { SurfacePlaceholder } from './components/placeholders/SurfacePlaceholder';

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  // [IF] single dark theme — the Admin Console look
  themes: [
    {
      id: 'intelligent-fabric',
      title: 'Intelligent Fabric',
      variant: 'dark',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={intelligentFabricTheme} children={children} />
      ),
    },
  ],
  // [IF] Keycloak OIDC only — no guest provider (security-sweep CRITICAL-4)
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        auto
        provider={{
          id: 'oidc',
          title: 'Keycloak',
          message: 'Sign in with your Intelligent Fabric account',
          apiRef: oidcAuthApiRef,
        }}
      />
    ),
  },
});

// [IF] interim links reused across placeholder surfaces
const GRAFANA = 'https://grafana.fabric.internal';

const routes = (
  <FlatRoutes>
    {/* [IF] branded home */}
    <Route path="/" element={<Navigate to="home" />} />
    <Route path="/home" element={<HomePage />} />

    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />

    {/* [IF] operator surfaces — placeholders until the Phase 30/31 plugins ship */}
    <Route
      path="/observability"
      element={
        <SurfacePlaceholder
          title="Observability & Audit"
          subtitle="JetStream · audit trail · LLM cost"
          phase="Phase 31 (BS-PLUGIN-03)"
          description="NATS stream/consumer/DLQ inspection and the cedar-audit trail without kubectl or a NATS CLI, plus per-agent LLM cost."
          links={[
            { title: 'Grafana — JetStream dashboard', to: GRAFANA, external: true },
            { title: 'Grafana — Cedar audit analytics', to: GRAFANA, external: true },
          ]}
        />
      }
    />
    <Route
      path="/identity"
      element={
        <SurfacePlaceholder
          title="Identity & Access"
          subtitle="Keycloak humans · Vault workloads"
          phase="Phase 30/32"
          description="The two trust domains — Keycloak group membership for humans, Vault JWT identities for agents and MCP workloads — in one view."
          links={[{ title: 'Catalog — Groups & Users', to: '/catalog?filters%5Bkind%5D=group' }]}
        />
      }
    />
    <Route
      path="/policy"
      element={
        <SurfacePlaceholder
          title="Policy Explorer"
          subtitle="Cedar policies per agent & tool"
          phase="Phase 31 (BS-PLUGIN-02)"
          description="Select an agent and see the Cedar policies that reference its namespace and the tools it may call, plus the catalog-gating Audit→Enforce state."
          links={[{ title: 'Catalog — Agents', to: '/catalog?filters%5Bkind%5D=component' }]}
        />
      }
    />
    <Route
      path="/compliance"
      element={
        <SurfacePlaceholder
          title="Compliance"
          subtitle="ISO 27001 · ISO 42001 · EU AI Act"
          phase="Phase 30 (BS-SCORE-*)"
          description="Per-service and per-agent scorecards via Tech Insights, where every check cites a live Prometheus metric, K8s API call, or Cedar audit row — never mere catalog existence."
          links={[{ title: 'Grafana — Cedar analytics', to: GRAFANA, external: true }]}
        />
      }
    />
    <Route
      path="/approvals"
      element={
        <SurfacePlaceholder
          title="Approvals (HITL)"
          subtitle="Human-in-the-loop gating"
          phase="Phase 24 (ACP-04)"
          description="The queue of infra-mutation-class actions awaiting a human; on approve, the approver's Keycloak identity is written into the Cedar audit row."
          links={[]}
        />
      }
    />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
